import axios from 'axios';
import { setupAxiosProgramInterceptor } from './setupAxios';
import { isAccessPendingError, routeToAccessPending } from '../lib/accessPendingRedirect';
import { isAuthVerificationBlockingRedirect } from '../lib/authVerificationState';
import { expireProductSession } from '../lib/sessionExpiry';
import { assertOwnedBackendRequest, productApiOrigin } from '../config/apiOrigin';
import { operationalApiRouteFamily, recordOperationalEvent } from '../analytics/operationalRuntime';

const client = axios.create({
    baseURL: productApiOrigin,
    timeout: 10000, // 10s global timeout (Enforced per user request)
});

setupAxiosProgramInterceptor(client);

// Request Interceptor: Attach Token
client.interceptors.request.use(
    (config) => {
        assertOwnedBackendRequest(config.url, config.baseURL, productApiOrigin);
        const token = localStorage.getItem('access_token');
        const explicitAuthorization = config.headers?.get?.('Authorization')
            ?? config.headers?.Authorization
            ?? config.headers?.authorization;
        if (token && !explicitAuthorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response Interceptor: Handle Global Errors (like 401)
client.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response && error.response.status;
        if (!error.response) {
            const apiRouteFamily = operationalApiRouteFamily(error.config?.url);
            if (apiRouteFamily) {
                void recordOperationalEvent({
                    event: 'cors_blocked', navigationId: crypto.randomUUID(), apiRouteFamily,
                    reasonCategory: window.navigator.onLine === false ? 'network' : 'cors',
                });
            }
        }
        if (status === 401) {
            if (isAuthVerificationBlockingRedirect()) {
                return Promise.reject(error);
            }
            console.warn('[API] Unauthorized - expiring the Product session.');
            const authorization = String(error.config?.headers?.Authorization ?? '');
            const rejectedToken = authorization.startsWith('Bearer ')
                ? authorization.slice('Bearer '.length)
                : null;
            expireProductSession(undefined, rejectedToken);
        } else if (isAccessPendingError(error)) {
            // AuthContext-owned /api/users/me verification requests pass
            // skipAccessPendingRedirect so THIS interceptor is not a second
            // navigation owner (ENG-R2-001). AuthContext's own catch routes
            // those with the reviewed returnTo. Request-scoped, matching the
            // 401 isAuthVerificationBlockingRedirect opt-out above.
            if (error.config?.skipAccessPendingRedirect) {
                return Promise.reject(error);
            }
            // Authenticated but not yet allowlisted (closed beta). Route any gated
            // API call to the redeem page, preserving the current page through
            // the canonical access-pending redirect helper.
            routeToAccessPending({ nextSource: 'current-page' });
        }
        return Promise.reject(error);
    }
);

export default client;
